import { faUnlock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelSelectItem from '../../../components/voxel/Forms/VoxelSelectItem';
import VoxelAdvancedTextSearch from '../../../components/voxel/Search/VoxelAdvancedTextSearch';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { PublicUserGq } from '../../../graphql/users/models/PublicUserGq';
import { CommandGq } from '../../../graphql/users/queries/useGetTeamConnectionsGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';

export interface AddTeamAccessDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  reload: () => Promise<unknown>;
  project: ProjectGq;
  user: PublicUserGq;
}

export const AddTeamAccessDialog: React.FC<AddTeamAccessDialogProps> = ({
  open,
  setOpen,
  project,
  reload,
  user,
}) => {
  // Team search
  const [childTeamSearchTerm, setChildTeamSearchTerm] = useState('');
  const debouncedChildSearchTerm = useDebounceValue(childTeamSearchTerm, 200);
  const childCommand = useMemo(
    (): CommandGq => ({
      searchTerm: debouncedChildSearchTerm,
    }),
    [debouncedChildSearchTerm]
  );

  const { query: childQuery, teamConnections: childTeams } =
    usersApiService.queries.useGetTeamConnections(childCommand);

  // Team access mutation
  const editProjectAccessMutation = usersApiService.mutations.useEditProjectAccess();

  const [selecetdTeamId, setSelectedTeamId] = useState<number>(null);

  const handleReload = useCallback(
    () => Promise.all<unknown>([childQuery.refetch(), reload()]),
    [childQuery.refetch, reload]
  );

  // Close
  const handleClose = () => {
    setOpen(false);
    setChildTeamSearchTerm('');
    setSelectedTeamId(null);
  };

  const handleAddAccess = (teamId: number) => {
    editProjectAccessMutation({
      teamId: teamId,
      removeProjectIds: [],
      addProjectIds: [project.id],
    })
      .then(() => handleReload())
      .finally(() => {
        handleClose();
      });
  };

  return (
    <VoxelDialog
      open={open}
      onClose={handleClose}
      titleIcon={<FontAwesomeIcon icon={faUnlock} />}
      title={'Add team access'}
      subtitle={'You can only grant project access to connected teams'}
    >
      <VoxelDialogContent>
        <VoxelAdvancedTextSearch
          size="sm"
          placeholder="Team name"
          value={childTeamSearchTerm}
          onChange={(event) => setChildTeamSearchTerm(event.target.value)}
          autoFocus
        />
      </VoxelDialogContent>
      <VoxelDialogContent
        className={'w-full flex flex-col h-96 max-h-96 min-h-max overflow-y-auto'}
      >

        {!!childTeams?.filter(
          (ct) => !project.grantedTeamAccess.some((ta) => ta.team?.id === ct.sharingTeam?.id)
        )?.length ? (
          <div className="py-4 w-full">
            {childTeams
              ?.filter(
                (ct) => !project.grantedTeamAccess.some((ta) => ta.team?.id === ct.sharingTeam?.id)
              )
              ?.map((ct, index) => (
                <VoxelSelectItem
                  key={ct.id}
                  selected={ct.sharingTeam?.id === selecetdTeamId}
                  onClick={() => setSelectedTeamId(ct.sharingTeam?.id)}
                  title={ct.sharingTeam?.name}
                  active={false}
                >
                  <div className="flex flex-row items-center gap-4 px-1.5 py-0.5">
                    <VoxelAvatar name={ct.sharingTeam?.name} size="sm" />
                    {ct.sharingTeam?.name}
                  </div>
                </VoxelSelectItem>
              ))}
          </div>
        ) : (
          <div className="py-4 w-full">
            <VoxelText className="italic">
              No connected connected teams without access found
            </VoxelText>
          </div>
        )}
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel />
        <VoxelDialogActionSubmit
          onClick={() => handleAddAccess(selecetdTeamId)}
          title={'Grant access'}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default AddTeamAccessDialog;
